import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '../components/ui';
import PrivacyNoticeModal from '../components/ui/PrivacyNoticeModal';

const PRIVACY_NOTICE_VERSION = '1.0';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [formData, setFormData] = useState({
    displayName: '',
    privacyNoticeReadConsent: false,
    dataProcessingConsent: false
  });
  
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If no consent property in user profile, redirect to chat (skip onboarding)
  React.useEffect(() => {
    if (user && !user.consent) {
      navigate('/chat');
    }
  }, [user, navigate]);

  // Don't render onboarding if no consent configuration
  if (!user?.consent) {
    return null;
  }

  const isFormValid = formData.displayName.trim().length >= 3 && 
                       formData.privacyNoticeReadConsent && 
                       formData.dataProcessingConsent;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!formData.privacyNoticeReadConsent) {
      setError('Please confirm that you have read the privacy notice.');
      return;
    }

    if (!formData.dataProcessingConsent) {
      setError('Please consent to data processing to continue.');
      return;
    }

    if (formData.displayName.trim().length < 3) {
      setError('Please enter at least 3 characters for your display name.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save the profile data including onboarding completion
      await userService.completeOnboarding({
        displayName: formData.displayName.trim(),
        metadata: {
          onboardingCompleted: true,
          privacyNoticeReadConsent: formData.privacyNoticeReadConsent,
          dataProcessingConsent: formData.dataProcessingConsent,
          privacyNoticeVersion: PRIVACY_NOTICE_VERSION,
          lastOnboardingUpdated: new Date().toISOString()
        }
      });
      
      // Force refresh user context to reflect the changes
      // This ensures we get the updated user profile with onboardingCompleted: true
      userService.clearCache(); // Clear cache to force backend fetch
      await refreshUser();
      
      // Navigate to chat
      navigate('/chat');
    } catch (error) {
      console.error('Onboarding failed:', error);
      setError('Failed to complete onboarding. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-rm-bg-0 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-card">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-semibold text-rm-primary-700">
              Welcome! Let's get started
            </CardTitle>
            <p className="mt-2 text-sm text-rm-neutral-600">
              Help us personalize your experience
            </p>
          </CardHeader>
          
          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="p-4 text-red-700 bg-red-50 border border-red-200 rounded-card">
                  {error}
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-rm-ink-900 mb-2">
                    Display Name / Nickname *
                  </label>
                  <Input
                    id="displayName"
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="How would you like to be called?"
                    required
                    minLength={3}
                    maxLength={50}
                  />
                  <p className="mt-1 text-xs text-rm-neutral-600">
                    Enter at least 3 characters ({formData.displayName.trim().length}/3 minimum)
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      id="privacyRead"
                      type="checkbox"
                      checked={formData.privacyNoticeReadConsent}
                      onChange={(e) => setFormData({ ...formData, privacyNoticeReadConsent: e.target.checked })}
                      className="h-4 w-4 mt-1 flex-shrink-0 text-rm-accent-600 border-rm-neutral-600 rounded focus:ring-rm-accent-500"
                      required
                    />
                    <label htmlFor="privacyRead" className="ml-2 block text-sm text-rm-ink-900">
                      I have read the{' '}
                      <a
                        href={user.consent.privacyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rm-accent-600 hover:text-rm-accent-700 underline font-medium"
                      >
                        Privacy Notice
                      </a>
                    </label>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      id="dataProcessing"
                      type="checkbox"
                      checked={formData.dataProcessingConsent}
                      onChange={(e) => setFormData({ ...formData, dataProcessingConsent: e.target.checked })}
                      className="h-4 w-4 mt-1 flex-shrink-0 text-rm-accent-600 border-rm-neutral-600 rounded focus:ring-rm-accent-500"
                      required
                    />
                    <label htmlFor="dataProcessing" className="ml-2 block text-sm text-rm-ink-900">
                      {user.consent.text}
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading || !isFormValid}
                  className="w-full"
                >
                  {isLoading ? 'Setting up...' : 'Complete Setup'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      <PrivacyNoticeModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
    </div>
  );
};

export default Onboarding;
